import {
  LS_KEY_AUTH_ISSUER,
  LS_KEY_TERMS_CONSENT,
  ROLE_PLAMO,
  SS_KEY_PENDING_LOGIN_HINT,
  SS_KEY_SKIP_SESSION_PROBE,
} from '@/const';
import { IdToken } from '@auth0/auth0-react';

export type UserInfo = {
  userId: string;
  name: string;
  email: string;
  picture?: string;
  tenantId: string;
  tenantName: string;
  roles: string[];
};

export function isAuthorized(userInfo: UserInfo): boolean {
  return userInfo.roles.includes(ROLE_PLAMO);
}

export function isAdmin(userInfo: UserInfo): boolean {
  return userInfo.roles.includes(ROLE_PLAMO + '/admin');
}

export function isPersonalTenant(userInfo: UserInfo): boolean {
  return userInfo.tenantId.startsWith('usr_');
}

export function isInternalUser(userInfo: UserInfo): boolean {
  return userInfo.roles.includes('internal');
}

function claimKey(claim: string): string {
  const namespace = import.meta.env.VITE_AUTH0_NAMESPACE;
  return new URL(claim, namespace).toString();
}

export function newUserInfo(idToken: IdToken): UserInfo {
  return {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    userId: idToken.sub ?? '',
    name: idToken.name ?? '',
    email: idToken.email ?? '',
    picture: idToken.picture ?? '',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    tenantId: idToken[claimKey('tenant_id')] ?? '',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    tenantName: idToken[claimKey('tenant')] ?? '',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    roles: idToken[claimKey('roles')] ?? [],
  };
}

export type Issuer = 'paid' | 'free';

export function isFreeIssuerEnabled(): boolean {
  return !!import.meta.env.VITE_AUTH0_FREE_CLIENT_ID;
}

// null means the login screen has to resolve the issuer from the user's email.
export function resolveIssuer(search: string): Issuer | null {
  if (!isFreeIssuerEnabled()) {
    return 'paid';
  }
  const param = new URLSearchParams(search).get('issuer');
  if (param === 'paid' || param === 'free') {
    storeIssuer(param);
  }
  if (!hasConsentedToTerms()) {
    return null;
  }
  const stored = localStorage.getItem(LS_KEY_AUTH_ISSUER);
  return stored === 'paid' || stored === 'free' ? stored : null;
}

// Set when the login form is submitted with the terms checkbox ticked. The terms
// ask for explicit consent, so until it is set the login screen ignores a stored
// issuer and skips the silent session probe. PLaMo Chat asks for the same terms,
// so consent is shared with it through a cookie on the parent domain; hosts
// outside that domain (the dev console, localhost) keep it per app.
export function hasConsentedToTerms(): boolean {
  try {
    if (localStorage.getItem(LS_KEY_TERMS_CONSENT) !== null) {
      // Consent recorded before the cookie existed reaches PLaMo Chat too.
      shareTermsConsent();
      return true;
    }
    return document.cookie.split('; ').includes(`${COOKIE_TERMS_CONSENT}=1`);
  } catch {
    return false;
  }
}

export function recordTermsConsent() {
  localStorage.setItem(LS_KEY_TERMS_CONSENT, '1');
  shareTermsConsent();
}

// Must match chat_ui/web/app/src/lib/issuer.ts.
const COOKIE_TERMS_CONSENT = 'plamo_terms_consent';
const SHARED_COOKIE_DOMAIN = 'preferredai.jp';

function shareTermsConsent() {
  const host = window.location.hostname;
  if (host !== SHARED_COOKIE_DOMAIN && !host.endsWith(`.${SHARED_COOKIE_DOMAIN}`)) {
    return;
  }
  // 400 days is the longest lifetime browsers keep; every load refreshes it.
  document.cookie = `${COOKIE_TERMS_CONSENT}=1; Domain=${SHARED_COOKIE_DOMAIN}; Path=/; Max-Age=34560000; Secure; SameSite=Lax`;
}

export function storeIssuer(issuer: Issuer) {
  localStorage.setItem(LS_KEY_AUTH_ISSUER, issuer);
}

export function clearIssuer() {
  localStorage.removeItem(LS_KEY_AUTH_ISSUER);
}

export function getPendingLoginHint(): string | null {
  return sessionStorage.getItem(SS_KEY_PENDING_LOGIN_HINT);
}

export function setPendingLoginHint(email: string) {
  sessionStorage.setItem(SS_KEY_PENDING_LOGIN_HINT, email);
}

export function takePendingLoginHint(): string | null {
  const email = getPendingLoginHint();
  sessionStorage.removeItem(SS_KEY_PENDING_LOGIN_HINT);
  return email;
}

// Set on an explicit user logout: Auth0 ends only that tenant's session, so the
// next login screen skips its silent session probe once to avoid a deliberate
// logout being undone by the other tenant's still-alive session. Automatic
// re-judgment logouts (token failure, personal tenant inactive) do not set it.
export function setSkipSessionProbe() {
  sessionStorage.setItem(SS_KEY_SKIP_SESSION_PROBE, '1');
}

export function takeSkipSessionProbe(): boolean {
  const skipped = sessionStorage.getItem(SS_KEY_SKIP_SESSION_PROBE) !== null;
  sessionStorage.removeItem(SS_KEY_SKIP_SESSION_PROBE);
  return skipped;
}

// Call before logging out: the next load re-resolves the issuer for this email on the login screen.
export function prepareRejudgment(email: string | undefined) {
  if (isFreeIssuerEnabled() && email) {
    setPendingLoginHint(email);
  }
  clearIssuer();
}

// Follows only the API gateway's URLs, so a crafted link cannot send the user elsewhere.
export function toGatewayUrl(url: string | undefined): string | null {
  if (!url) {
    return null;
  }
  try {
    const parsed = new URL(url);
    return parsed.origin === new URL(import.meta.env.VITE_API_GATEWAY_URL).origin
      ? parsed.toString()
      : null;
  } catch {
    return null;
  }
}
